import { apiService } from './apiService';

export const supplyService = {
  async getAll(params?: { status?: string; shipmentId?: string; updatedSince?: string; externalTransportAccountUserId?: string; includeLocked?: boolean }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.shipmentId) queryParams.append('shipmentId', params.shipmentId);
    if (params?.updatedSince) queryParams.append('updatedSince', params.updatedSince);
    if (params?.externalTransportAccountUserId) queryParams.append('externalTransportAccountUserId', params.externalTransportAccountUserId);
    if (params?.includeLocked) queryParams.append('includeLocked', '1');
    
    const query = queryParams.toString();
    return await apiService.get(`/supply-records${query ? `?${query}` : ''}`, { timeoutMs: 300000 });
  },

  async getById(id: string | number) {
    return await apiService.get(`/supply-records/${id}`);
  },

  async create(data: any) {
    return await apiService.post('/supply-records', data);
  },

  async update(id: string | number, data: any) {
    return await apiService.put(`/supply-records/${id}`, data);
  },

  async delete(id: string | number) {
    return await apiService.delete(`/supply-records/${id}`);
  },

  async updateStatus(id: string | number, status: string) {
    return await apiService.put(`/supply-records/${id}/status`, { status });
  },

  async updateProcessStatus(id: string | number, processStatus: string) {
    return await apiService.put(`/supply-records/${id}/process-status`, { processStatus });
  },

  async bulkUpdateProcessStatus(ids: Array<string | number>, processStatus: string) {
    return await apiService.put('/supply-records/bulk-process-status', { ids, processStatus });
  },

  async bulkUpdateServiceType(ids: Array<string | number>, serviceTypes: string[]) {
    return await apiService.put('/supply-records/bulk-service-type', { ids, serviceTypes });
  },

  async setDemurrageManualEdit(ids: Array<string | number>, enabled: boolean) {
    return await apiService.post('/supply-records/demurrage/manual-edit', { shipmentIds: ids, enabled });
  },

  async updateFreight(id: string | number, data: { freightAmount: number; reasonCode: string; reasonText: string; note?: string }) {
    return await apiService.put(`/supply-records/${id}/freight`, data);
  },

  async advanceProcessStatus(id: string | number) {
    return await apiService.put(`/supply-records/${id}/advance-process`, {});
  },

  async revertProcessStatus(id: string | number) {
    return await apiService.put(`/supply-records/${id}/revert-process`, {});
  },

  async checkLocationStatus(id: string | number, target?: 'pickup' | 'delivery' | 'auto') {
    const query = target ? `?target=${target}` : '';
    return await apiService.get(`/supply-records/${id}/location-check${query}`);
  },

  async assignVehicle(id: string | number, vehicleId: string | number, driverId?: string | number) {
    return await apiService.put(`/supply-records/${id}/assign`, { assignmentType: 'own-fleet', vehicleId, driverId });
  },

  async assignSupply(id: string | number, data: { assignmentType: 'own-fleet' | 'spot'; vehicleId?: string | number; applicationId?: string | number; driverId?: string | number; agreedPrice?: number }) {
    return await apiService.put(`/supply-records/${id}/assign`, data);
  },

  async sendAssignmentTestEmail(id: string | number) {
    return await apiService.post(`/supply-records/${id}/test-assignment-email`, {});
  },

  async unassignSupply(id: string | number) {
    return await apiService.put(`/supply-records/${id}/unassign`, {});
  },

  async recalculateNearestHubs(missingOnly = true) {
    return await apiService.post(`/supply-records/recalculate-nearest-hubs?missingOnly=${missingOnly ? 'true' : 'false'}`, {});
  },

  async recalculateRouteDistances() {
    return await apiService.post('/supply-records/recalculate-route-distances', {}, { timeoutMs: 300000 });
  },

  async backfillLkm() {
    return await apiService.post('/supply-records/backfill-lkm', {}, { timeoutMs: 300000 });
  },

  async backfillFarthestDeliveryPoints(limit = 100, afterId = 0) {
    return await apiService.post(`/supply-records/backfill-farthest-delivery-points?limit=${limit}&afterId=${afterId}`, {}, { timeoutMs: 300000 });
  },

  async backfillRemoveDuplicateStops(limit = 200, afterId = 0) {
    return await apiService.post(`/supply-records/backfill-remove-duplicate-stops?limit=${limit}&afterId=${afterId}`, {}, { timeoutMs: 300000 });
  },

  async backfillRouteBasedFinancialPrices(limit = 25, _afterId = 0, shipmentNo = '') {
    const query = new URLSearchParams({ limit: String(limit) });
    if (shipmentNo.trim()) query.set('shipmentNo', shipmentNo.trim());
    return await apiService.post(`/supply-records/backfill-route-based-financial-prices?${query.toString()}`, {}, { timeoutMs: 300000 });
  },

  async syncFromWorkiom() {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/workiom/sync-supply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(300000),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Workiom senkronizasyonu başarısız');
    }
    return data;
  },

  async previewFromWorkiom() {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/workiom/preview-supply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(300000),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Workiom önizleme başarısız');
    }
    return data;
  },

  async previewDecisionSupportImport(data: { shipments: any[]; distanceLimitKm?: number | null; replicationCount?: number | null; searchMethod?: string | null; distanceProvider?: string | null; ignoreVehicleStatusForMatchingDepot?: boolean }) {
    return await apiService.post('/decision-support/preview-import', data, { timeoutMs: 180000 });
  },

  async estimatePreviewDecisionSupportImport(data: { shipments: any[]; distanceLimitKm?: number | null; distanceProvider?: string | null; ignoreVehicleStatusForMatchingDepot?: boolean }) {
    return await apiService.post('/decision-support/preview-import/estimate', data, { timeoutMs: 180000 });
  },

  async getPreviewDecisionSupportImportStatus(jobId: string) {
    return await apiService.get(`/decision-support/preview-import/${jobId}`, { timeoutMs: 180000 });
  },

  async cancelPreviewDecisionSupportImport(jobId: string) {
    return await apiService.delete(`/decision-support/preview-import/${jobId}`);
  },
};
